import {
  Image,
  Linking,
  Modal,
  ScrollView,
  Text,
  View,
  type ImageSourcePropType,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { ClipboardList, ExternalLink, Info, X } from "lucide-react-native";
import { IconButton, SectionTitle } from "@louise/moon-ui";
import { CarePillActionButton } from "@src/packages/homepage/plan/components/CarePillActionButton";
import { useTranslations } from "@src/packages/i18n";
import {
  claimEffectiveLabel,
  isFinancialClaimCurrent,
  type FinancialClaim,
  type FinancingProvider,
} from "@src/packages/costs/financialContent";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import FactSheetHeader from "../../components/FactSheetHeader";
import { EMBER } from "../../../onboarding/theme";

type Props = {
  provider: FinancingProvider | null;
  logo: ImageSourcePropType | null;
  onClose: () => void;
};

function ClaimBlock({ claim }: { claim: FinancialClaim }) {
  return (
    <View
      style={{
        padding: 13,
        borderRadius: 14,
        backgroundColor: "rgba(255,255,255,0.46)",
      }}
    >
      <Text
        className="font-dm-sans"
        style={{ fontSize: 12, lineHeight: 16, color: EMBER.colors.textLight }}
      >
        {claim.label}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          marginTop: 4,
          fontSize: 14,
          lineHeight: 20,
          fontWeight: "600",
          color: EMBER.colors.text,
        }}
      >
        {claim.value}
      </Text>
      {claim.caveat ? (
        <Text
          className="font-dm-sans"
          style={{
            marginTop: 5,
            fontSize: 12,
            lineHeight: 17,
            color: EMBER.colors.textMid,
          }}
        >
          {claim.caveat}
        </Text>
      ) : null}
      <Text
        className="font-dm-sans"
        style={{
          marginTop: 7,
          fontSize: 10,
          lineHeight: 14,
          color: EMBER.colors.textFaint,
        }}
      >
        {claimEffectiveLabel(claim)}
      </Text>
    </View>
  );
}

export default function FinancingFactSheet({ provider, logo, onClose }: Props) {
  const t = useTranslations();
  const insets = useSafeAreaInsets();

  if (!provider) return null;

  const currentClaims = provider.claims.filter((claim) =>
    isFinancialClaimCurrent(claim),
  );

  return (
    <Modal visible transparent animationType="none" onRequestClose={onClose}>
      <View style={{ flex: 1, overflow: "hidden" }}>
        <Image
          source={require("@louise/moon-ui/assets/images/onboarding-bg.jpg")}
          style={{
            position: "absolute",
            width: "115%",
            height: "115%",
            left: "-7.5%",
            top: "-7.5%",
          }}
          resizeMode="cover"
        />

        <ScrollView
          contentContainerStyle={{
            paddingHorizontal: 16,
            paddingTop: insets.top + SCREEN_PADDING_TOP,
            paddingBottom: 100,
            maxWidth: 520,
            width: "100%",
            alignSelf: "center",
          }}
          showsVerticalScrollIndicator={false}
        >
          <View
            style={{
              ...GLASS_CARD_STYLE,
              paddingHorizontal: 24,
              paddingTop: 24,
              paddingBottom: 32,
            }}
          >
            <View
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 2,
              }}
            >
              <IconButton
                onPress={onClose}
                hitSlop={8}
                size={32}
                surface="soft"
                backgroundColor="rgba(0,0,0,0.05)"
                pressedBackgroundColor="rgba(0,0,0,0.08)"
                pressedOpacity={1}
                accessibilityLabel={t("costs.financing_page.close_fact_sheet")}
              >
                <X size={16} color={EMBER.colors.textLight} />
              </IconButton>
            </View>

            <FactSheetHeader
              title={provider.name}
              subtitle={provider.productLabel}
              imageSource={logo ?? undefined}
              imageSize={92}
            />

            <View
              style={{
                marginTop: 24,
                padding: 14,
                borderRadius: 14,
                backgroundColor: "rgba(100, 54, 32, 0.07)",
              }}
            >
              <SectionTitle icon={Info}>
                {t("costs.financing_page.fact_sheet_summary_heading")}
              </SectionTitle>
              <Text
                className="font-dm-sans"
                style={{
                  marginTop: 8,
                  fontSize: 15,
                  lineHeight: 21,
                  color: EMBER.colors.text,
                }}
              >
                {provider.summary.value}
              </Text>
              {provider.summary.caveat ? (
                <Text
                  className="font-dm-sans"
                  style={{
                    marginTop: 5,
                    fontSize: 12,
                    lineHeight: 17,
                    color: EMBER.colors.textMid,
                  }}
                >
                  {provider.summary.caveat}
                </Text>
              ) : null}
            </View>

            <View style={{ marginTop: 24, marginBottom: 10 }}>
              <SectionTitle icon={ClipboardList}>
                {t("costs.financing_page.fact_sheet_details_heading")}
              </SectionTitle>
            </View>

            {currentClaims.length ? (
              <View style={{ gap: 12 }}>
                {currentClaims.map((claim) => (
                  <ClaimBlock key={claim.id} claim={claim} />
                ))}
              </View>
            ) : (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  lineHeight: 19,
                  color: EMBER.colors.textMid,
                }}
              >
                {t("costs.financing_page.fact_sheet_unavailable")}
              </Text>
            )}

            <Text
              className="font-dm-sans"
              style={{
                marginTop: 24,
                fontSize: 11,
                lineHeight: 16,
                color: EMBER.colors.textFaint,
                textAlign: "center",
              }}
            >
              {t("costs.financing_page.fact_sheet_reviewed", {
                params: {
                  date: provider.summary.provenance.lastVerifiedOn,
                  jurisdiction: provider.summary.provenance.jurisdiction,
                },
              })}
            </Text>

            <CarePillActionButton
              label={t("costs.financing_page.read_provider_terms")}
              onPress={() => Linking.openURL(provider.url)}
              icon={ExternalLink}
              variant="primary"
              flexGrow={1}
              minHeight={44}
            />
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
}
